import moment from "moment";
import { SSC_SEM_API_URL } from "../../settings/config";
import { getSelectedWebsiteGuid, getSelectedWebsiteDomain } from "../tracked_websites/selectors";
import { addedKeywords, keywordOverviewInProgress, getTopDomains } from "../tracked_keywords/selectors";
import { authRequest } from "../authorizedRequests";
import {
  fetchTrackedKeywordsInProgress,
  fetchTrackedKeywordsSuccessful,
  fetchTrackedKeywordsFailed,
  fetchTrackedKeywordsCountInProgress,
  fetchTrackedKeywordsCountSuccessful,
  fetchTrackedKeywordsCountFailed,
  fetchTopDomainsInProgress,
  fetchTopDomainsSuccessful,
  fetchTopDomainsFailed,
  fetchKeywordOverviewInProgress,
  fetchKeywordOverviewSuccessful,
  fetchKeywordOverviewFailed,
  addKeywordsInProgress,
  addKeywordsSuccessful,
  addKeywordsFailed,
  changeSeoDbInProgress,
  changeSeoDbSuccessful,
  changeSeoDbFailed,
  deleteKeywordInProgress,
  deleteKeywordSuccessful,
  deleteKeywordFailed
} from "./creators";

export const fetchTrackedKeywordsCount = () => {
  return (dispatch, getState) => {
    const apiUrl = `${SSC_SEM_API_URL}/tracked-keywords/count`;

    dispatch(fetchTrackedKeywordsCountInProgress());

    authRequest(apiUrl, dispatch, getState)
      .then(response => {
        dispatch(fetchTrackedKeywordsCountSuccessful(response.keywords_no));
      })
      .catch(_ => {
        dispatch(fetchTrackedKeywordsCountFailed());
      });
  };
};

export const fetchTrackedKeywords = (guid, domain) => {
  return (dispatch, getState) => {
    const apiUrl = `${SSC_SEM_API_URL}/tracked-keywords?domain=${domain}`;
    dispatch(fetchTrackedKeywordsInProgress(guid));
    return authRequest(apiUrl, dispatch, getState)
      .then(response => {
        dispatch(fetchTrackedKeywordsSuccessful(guid, response.keywords, response.seo_db));
      })
      .catch(_ => {
        dispatch(fetchTrackedKeywordsFailed(guid));
      });
  };
};

export const fetchTopDomains = (keyword, seoDb) => {
  return (dispatch, getState) => {
    const apiUrl = `${SSC_SEM_API_URL}/top-domains?seo_db=${seoDb}&phrase=${keyword}`;
    const hKey = `${keyword}:${seoDb}`;
    const topDomains = getTopDomains(getState(), hKey);
    const domainGuid = getSelectedWebsiteGuid(getState());

    if (topDomains && Object.keys(topDomains).length) {
      return Promise.resolve(topDomains);
    }

    dispatch(fetchTopDomainsInProgress(hKey));

    authRequest(apiUrl, dispatch, getState)
      .then(response => {
        dispatch(fetchTopDomainsSuccessful(hKey, domainGuid, keyword, response.top_domains));
      })
      .catch(_ => {
        dispatch(fetchTopDomainsFailed(hKey));
      });
  };
};

export const fetchKeywordOverviews = (domain, keyword, seoDb) => {
  return (dispatch, getState) => {
    const hKey = `${domain}:${keyword}:${seoDb}`;

    if (getState().tracked_keywords.overviews[hKey]) {
      return Promise.resolve();
    }

    const domainGuid = getSelectedWebsiteGuid(getState());
    const endDate = moment().utc();
    const endDateStr = endDate.format("YYYY-MM-DDTHH:mm:ss");
    const startDate = endDate.subtract(12, "months");
    const startDateStr = startDate.format("YYYY-MM-DDTHH:mm:ss");
    const apiUrl = `${SSC_SEM_API_URL}/keyword-overview?seo_db=${seoDb}&phrase=${keyword}&domain=${domain}&start_date=${startDateStr}&end_date=${endDateStr}`;

    dispatch(fetchKeywordOverviewInProgress(hKey));

    authRequest(apiUrl, dispatch, getState)
      .then(response => {
        dispatch(fetchKeywordOverviewSuccessful(hKey, domainGuid, keyword, response.overviews));
      })
      .catch(_ => {
        dispatch(fetchKeywordOverviewFailed(hKey));
      });
  };
};

export const addTrackedKeywords = (domain, seo_db, keywords, start_monitor = false) => {
  return (dispatch, getState) => {
    if (keywords.length === 0) {
      Promise.resolve();
    }

    const apiUrl = `${SSC_SEM_API_URL}/tracked-keywords/batch`;
    const payload = {
      domain: domain,
      db: seo_db,
      keywords: keywords,
      start_monitor: start_monitor
    };

    dispatch(addKeywordsInProgress(domain));

    return authRequest(apiUrl, dispatch, getState, {
      method: "POST",
      body: JSON.stringify(payload)
    })
      .then(_ => {
        dispatch(addKeywordsSuccessful(domain));
      })
      .catch(error => {
        dispatch(addKeywordsFailed(domain));
        throw new Error(error);
      });
  };
};

export const addTrackedKeywordsAndCheckStatus = (domain, seo_db, keywords) => {
  return (dispatch, getState) => {
    const websiteGuid = getSelectedWebsiteGuid(getState());
    dispatch(addTrackedKeywords(domain, seo_db, keywords))
      .then(() => {
        dispatch(fetchTrackedKeywordsCount());
        Promise.resolve();
      })
      .then(body => {
        const fetchKeywordsInterval = setInterval(() => {
          if (!addedKeywords(getState(), keywords) || keywordOverviewInProgress(getState())) {
            dispatch(fetchTrackedKeywords(websiteGuid, domain));
          } else {
            clearInterval(fetchKeywordsInterval);
          }
        }, 1000);
      })
      .catch(_ => {});
  };
};

export const changeKeywordsSeoDb = (domain, newSeoDb, postAction) => {
  return (dispatch, getState) => {
    const apiUrl = `${SSC_SEM_API_URL}/tracked-keywords/change-seo-db`;
    const websiteGuid = getSelectedWebsiteGuid(getState());
    const payload = {
      domain: domain,
      new_db: newSeoDb
    };

    dispatch(changeSeoDbInProgress(websiteGuid));

    authRequest(apiUrl, dispatch, getState, {
      method: "PATCH",
      body: JSON.stringify(payload)
    })
      .then(response => {
        if (postAction === "reload_tracked_keywords") {
          dispatch(fetchTrackedKeywords(websiteGuid, response.domain)).then(_ => {
            dispatch(changeSeoDbSuccessful(websiteGuid, newSeoDb));
          });
        } else {
          dispatch(changeSeoDbSuccessful(websiteGuid, newSeoDb));
        }
      })
      .then(_ => {
        if (postAction === "reload_tracked_keywords") {
          const changeSeoDbInterval = setInterval(() => {
            if (keywordOverviewInProgress(getState())) {
              dispatch(fetchTrackedKeywords(websiteGuid, domain));
            } else {
              clearInterval(changeSeoDbInterval);
            }
          }, 1000);
        }
      })
      .catch(_ => {
        dispatch(changeSeoDbFailed(websiteGuid));
      });
  };
};

export const deleteKeyword = keyword => {
  return (dispatch, getState) => {
    const apiUrl = `${SSC_SEM_API_URL}/tracked-keywords`;
    const websiteGuid = getSelectedWebsiteGuid(getState());
    const domain = getSelectedWebsiteDomain(getState());

    dispatch(deleteKeywordInProgress(websiteGuid, keyword));

    authRequest(apiUrl, dispatch, getState, {
      method: "DELETE",
      body: JSON.stringify({ domain, keyword })
    })
      .then(_ => {
        dispatch(deleteKeywordSuccessful(websiteGuid, keyword));
      })
      .then(_ => {
        dispatch(fetchTrackedKeywordsCount());
      })
      .then(_ => {
        dispatch(fetchTrackedKeywords(websiteGuid, domain));
      })
      .catch(_ => {
        dispatch(deleteKeywordFailed(websiteGuid, keyword));
      });
  };
};
